import { AudienceDnaCard } from '@/components/analytics/AudienceDnaCard';
import { BrandMentionsCard } from '@/components/analytics/BrandMentionsCard';
import { CompetitorAnalysisCard } from '@/components/analytics/CompetitorAnalysisCard';
import { ContentMediaAnalysisCard } from '@/components/analytics/ContentMediaAnalysisCard';
import { MarketingFunnelCard } from '@/components/analytics/MarketingFunnelCard';
import { NewsJackingCard } from '@/components/analytics/NewsJackingCard';
import { CampaignSetupBot } from '@/components/shared/CampaignSetupBot';
import FacebookAccountSelect from '@/components/shared/FacebookAccountSelect';
import { NextPostCard } from '@/components/shared/NextPostCard';
import { OnboardingSocial } from '@/components/shared/OnboardingSocial';
import { OverviewCard } from '@/components/shared/OverviewCard';
import { PostPerformanceCard } from '@/components/shared/PostPerformanceCard';
import { TeamOnboardingBot } from '@/components/shared/TeamOnboardingBot';
import GradualSpacing from '@/components/ui/gradual-spacing';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useHomeScreenFeatures } from '@/hooks/useHomeScreenFeatures';
import { useTeamStatus } from '@/hooks/useTeamStatus';
import { copilotkitRuntimeUrl } from '@/utils/copilot';
import { CopilotKit } from '@copilotkit/react-core';
import { ReactElement } from 'react';
import Masonry from 'react-responsive-masonry';
import { useNavigate } from 'react-router-dom';

const getColumnCount = (width: number): number => {
  const breakpointColumns = {
    default: 3,
    1536: 3,
    1280: 2,
    768: 1,
  } as const;

  type BreakpointKey = keyof typeof breakpointColumns;

  const breakpoints = Object.entries(breakpointColumns)
    .filter(([key]) => key !== 'default')
    .sort(([a], [b]) => Number(b) - Number(a));

  const matchingBreakpoint = breakpoints.find(
    ([breakpoint]) => width >= Number(breakpoint),
  );

  return matchingBreakpoint
    ? breakpointColumns[matchingBreakpoint[0] as BreakpointKey]
    : breakpointColumns.default;
};

export const HomePage = (): ReactElement => {
  const navigate = useNavigate();
  const { data: campaigns } = useCampaigns();
  const {
    teamSocialsConnected,
    teamOnboardingComplete,
    hasFacebookAccount,
    hasSetupFirstCampaign,
  } = useTeamStatus();
  const hasHomeScreenFeatures = useHomeScreenFeatures();

  if (!teamSocialsConnected) {
    return <OnboardingSocial />;
  }

  if (!hasFacebookAccount) {
    return <FacebookAccountSelect />;
  }

  const showTeamOnboardingBot = !teamOnboardingComplete;
  const showCampaignSetupBot = teamOnboardingComplete;
  const activeCampaignId = campaigns?.[0]?.id;

  const handleCampaignSuccess = (campaignId: string) => {
    navigate(`/calendar?campaign=${campaignId}`);
  };

  return (
    <>
      <div className="flex shrink-0">
        <GradualSpacing
          className="font-display text-center text-3xl font-bold -tracking-widest text-black dark:text-white md:text-5xl md:leading-[4rem]"
          text="Dashboard"
        />
      </div>

      <Masonry columnsCount={getColumnCount(window.innerWidth)} gutter="24px">
        {showTeamOnboardingBot ? (
          <CopilotKit runtimeUrl={copilotkitRuntimeUrl} key="team-onboarding">
            <TeamOnboardingBot />
          </CopilotKit>
        ) : showCampaignSetupBot ? (
          <CopilotKit runtimeUrl={copilotkitRuntimeUrl} key="campaign-setup">
            <CampaignSetupBot onSuccess={handleCampaignSuccess} />
          </CopilotKit>
        ) : null}
        <PostPerformanceCard />
        <OverviewCard />
        {hasSetupFirstCampaign && campaigns && campaigns.length > 0 && (
          <NextPostCard />
        )}
        {hasHomeScreenFeatures && (
          <>
            <NewsJackingCard />
            <CompetitorAnalysisCard />
            <AudienceDnaCard />
            <MarketingFunnelCard />
            <ContentMediaAnalysisCard />
            <BrandMentionsCard />
          </>
        )}
      </Masonry>
    </>
  );
};
